// Turns a raw Supabase/fetch error into a message a non-technical user can
// actually act on — "Failed to fetch" or a bare timeout means nothing to
// most people, but "check your internet connection" tells them what to do.
export function friendlyError(error, fallback = "Something went wrong. Try again.") {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return "You're offline. Check your internet connection and try again.";
  }

  const message = error?.message || "";
  const isNetworkError =
    message.includes("Failed to fetch") ||
    message.includes("NetworkError") ||
    message.includes("network") ||
    message.includes("timeout") ||
    error?.name === "AbortError";

  if (isNetworkError) {
    return "Network problem — check your connection and try again.";
  }

  // An expired session (PostgREST's PGRST301) needs its own actionable
  // message rather than falling into the generic fallback below — "try
  // again" is actively misleading here, since retrying the same action
  // does nothing until the user actually logs back in.
  const isSessionExpired = error?.code === "PGRST301" || message.includes("JWT expired");
  if (isSessionExpired) {
    return "Your session expired — please log in again.";
  }

  // Raw Postgres/PostgREST errors ("permission denied for table users",
  // "duplicate key value violates unique constraint ...", or PostgREST's
  // own PGRST116 "JSON object requested, multiple (or no) rows returned")
  // are meant for developers, not end users — the Postgres ones can hint at
  // schema details (table and column names) too, on top of just reading as
  // broken. A hand-written RPC exception (e.g. "must_be_18_or_older") uses
  // Postgres's generic P0001 code for a plain RAISE EXCEPTION with no
  // SQLSTATE of its own — excluded here, or this would swallow every custom
  // exception message the same way it catches a genuine system error like
  // 23505 (unique_violation) or 42501 (insufficient_privilege). The text
  // patterns below are similarly narrow (multi-word, unmistakably-Postgres
  // phrasing) rather than single common words like "violates" or
  // "constraint" alone, which a hand-written message could plausibly
  // contain on its own.
  const hasPgErrorCode =
    typeof error?.code === "string" &&
    error.code !== "P0001" &&
    (/^[0-9A-Z]{5}$/.test(error.code) || /^PGRST\d+$/.test(error.code));
  const looksLikeRawDbError =
    hasPgErrorCode ||
    /permission denied for|duplicate key value|violates (unique|foreign key|check|not-null) constraint|relation ".*" does not exist|column ".*" does not exist|syntax error at or near|row-level security policy/i.test(
      message
    );

  if (looksLikeRawDbError) {
    return fallback;
  }

  return message || fallback;
}
